import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Download } from "lucide-react";
import { useAdminApi, downloadCsv } from "./useAdminApi";
import { money } from "@/lib/api";

const Stat = ({ label, value, testid, accent }) => (
  <div className={`border-2 border-mas-ink p-5 ${accent || "bg-white"}`} data-testid={testid}>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] opacity-70">{label}</p>
    <p className="font-serif text-3xl font-black mt-2">{value}</p>
  </div>
);

export default function AdminOverview() {
  const api = useAdminApi();
  const [r, setR] = useState(null);
  useEffect(() => {
    api("get", "/admin/reports").then(setR);
  }, [api]);
  if (!r) return <div className="h-40 animate-pulse paper-card" />;
  const k = (x) => r.by_kind[x] || { total: 0, count: 0 };
  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Stat label="Raised toward goal" value={money(r.campaign.raised)} testid="admin-stat-raised" accent="bg-mas-terra text-mas-cream" />
        <Stat label="Remaining" value={money(r.campaign.remaining)} testid="admin-stat-remaining" />
        <Stat label="Goal progress" value={`${r.campaign.percent}%`} testid="admin-stat-percent" />
        <Stat label="Supporters" value={r.campaign.supporters} testid="admin-stat-supporters" />
        <Stat label={`Donations (${k("donation").count})`} value={money(k("donation").total, true)} testid="admin-stat-donations" />
        <Stat label={`Sponsorships (${k("sponsorship").count})`} value={money(k("sponsorship").total, true)} testid="admin-stat-sponsorships" />
        <Stat label={`Ticket sales (${k("ticket").count})`} value={money(k("ticket").total, true)} testid="admin-stat-tickets" />
        <Stat label="Monthly donors · pending approvals" value={`${r.recurring_donors} · ${r.pending_approvals}`} testid="admin-stat-recurring" accent="bg-mas-paper" />
      </div>
      <div className="bg-white border-2 border-mas-ink p-5">
        <p className="eyebrow mb-4">Monthly revenue</p>
        {r.monthly.length ? (
          <div className="h-72"><ResponsiveContainer>
            <BarChart data={r.monthly}><CartesianGrid strokeDasharray="3 3" stroke="#E2D5C3" /><XAxis dataKey="month" /><YAxis /><Tooltip /><Legend />
              <Bar dataKey="donation" stackId="a" fill="#C85A32" /><Bar dataKey="sponsorship" stackId="a" fill="#556B2F" /><Bar dataKey="ticket" stackId="a" fill="#D99B26" />
            </BarChart>
          </ResponsiveContainer></div>
        ) : <p className="text-sm text-mas-mute">No payments yet.</p>}
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border-2 border-mas-ink p-5">
          <p className="eyebrow mb-4">Tickets by tier</p>
          <table className="w-full text-sm"><thead><tr className="text-left text-mas-mute"><th className="py-1">Tier</th><th>Sold</th><th>Capacity</th><th>Revenue</th></tr></thead>
            <tbody>{r.tickets.map((t) => <tr key={t.name} className="border-t border-mas-sand"><td className="py-2">{t.name}</td><td>{t.sold}</td><td>{t.capacity || "∞"}</td><td>{money(t.revenue, true)}</td></tr>)}</tbody></table>
        </div>
        <div className="bg-white border-2 border-mas-ink p-5">
          <p className="eyebrow mb-4">Form submissions</p>
          <ul className="text-sm space-y-2">{["club_registration", "performer_application", "vendor_application", "volunteer", "newsletter"].map((t) => <li key={t} className="flex justify-between border-b border-mas-sand pb-1"><span className="capitalize">{t.replace("_", " ")}</span><strong>{r.submissions[t] || 0}</strong></li>)}</ul>
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        {["donation", "sponsorship", "ticket", "submissions"].map((w) => (
          <button key={w} onClick={() => downloadCsv(api, w)} className="btn-ink !py-2" data-testid={`admin-export-${w}`}><Download className="w-4 h-4" />Export {w} CSV</button>
        ))}
      </div>
    </div>
  );
}
